import { apiGet, apiPost, apiPut, apiDelete } from './apiUtils';

const crmService = {
  // ========== Contacts ==========
  getContacts: async (filters = {}) => {
    return await apiGet('/api/crm/contacts', { params: filters });
  },

  /**
   * Get builder's contacts (agents/agencies who shared leads)
   * @returns {Promise<Object>} - List of agents/agencies with partnership status
   */
  getBuilderContacts: async () => {
    try {
      const response = await apiGet('/api/crm/partnerships/contacts', {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('Builder Contacts API Response:', response);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching builder contacts:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder contacts'
      };
    }
  },

  getContactById: async (id) => {
    return await apiGet(`/api/crm/contacts/${id}`);
  },

  createContact: async (contactData) => {
    return await apiPost('/api/crm/contacts', contactData);
  },

  updateContact: async (id, contactData) => {
    return await apiPut(`/api/crm/contacts/${id}`, contactData);
  },

  deleteContact: async (id) => {
    return await apiDelete(`/api/crm/contacts/${id}`);
  },

  // ========== Leads ==========
  getLeads: async (filters = {}) => {
    try {
      // Ensure search parameter is properly encoded
      const processedFilters = { ...filters };
      if (processedFilters.search) {
        processedFilters.search = processedFilters.search.trim();
      }

      console.log('CRM Service - Getting Leads with filters:', processedFilters);

      const response = await apiGet('/api/crm/leads', processedFilters);

      console.log('CRM Service - Lead search response:', response);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching leads:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch leads'
      };
    }
  },

  getLeadById: async (id) => {
    return await apiGet(`/api/crm/leads/${id}`);
  },

  getOpenLeadDetails: async (leadId) => {
    try {
      console.group('🔍 getOpenLeadDetails');
      console.log('Fetching lead details for ID:', leadId);

      const response = await apiGet(`/api/crm/leads/${leadId}`);

      console.log('Raw Response:', response);
      console.log('Response Keys:', Object.keys(response));
      console.groupEnd();

      return {
        success: true,
        method: 'getOpenLeadDetails',
        data: response.data || response
      };
    } catch (error) {
      console.error('🚨 Error in getOpenLeadDetails:', error);
      return {
        success: false,
        method: 'getOpenLeadDetails',
        message: error.response?.data?.message || error.message || 'Failed to fetch lead details',
        error: error
      };
    }
  },

  createLead: async (leadData) => {
    try {
      const response = await apiPost('/api/crm/leads', leadData);

      // Check if the response itself is an error (like from apiUtils for 409 conflicts)
      if (response && response.hasOwnProperty('success') && response.success === false) {
        return response; // Return error response as-is
      }

      // Normal success case
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error creating lead:', error);

      // If apiUtils returned a structured error response (like 409 conflicts), pass it through
      if (error && typeof error === 'object' && error.hasOwnProperty('success')) {
        return error; // Return the structured error as-is
      }

      // For other errors, wrap in generic structure
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create lead'
      };
    }
  },

  updateLead: async (id, leadData) => {
    try {
      const response = await apiPut(`/api/crm/leads/${id}`, leadData);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error updating lead:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update lead'
      };
    }
  },

  updateLeadStatus: async (id, status) => {
    return await apiPut(`/api/crm/leads/${id}/status`, { status });
  },

  assignLead: async (leadId, userId) => {
    return await apiPost(`/api/crm/leads/${leadId}/assign`, { userId });
  },

  // ========== Invoices ==========
  getInvoices: async (filters = {}) => {
    try {
      console.log('🔄 CRM Service getInvoices called with filters:', filters);
      // Only pass params if filters are actually provided
      const hasFilters = Object.keys(filters).length > 0;
      console.log('📊 Has filters?', hasFilters);

      const response = hasFilters
        ? await apiGet('/api/crm/invoices/my', filters)
        : await apiGet('/api/crm/invoices/my');

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch invoices'
      };
    }
  },

  // ========== Feedback and Notes ==========
  addFeedback: async (leadId, feedback) => {
    return await apiPut(`/api/crm/leads/${leadId}`, { notes: feedback });
  },

  submitCallFeedback: async (leadId, feedbackData) => {
    return await apiPost(`/api/crm/leads/${leadId}/feedback`, feedbackData);
  },

  addLeadNote: async (leadId, note) => {
    return await apiPost(`/api/crm/leads/${leadId}/notes`, { note });
  },

  // ========== Tasks ==========
  createTask: async (taskData) => {
    return await apiPost('/api/tasks', taskData);
  },

  // ========== Site Visit Tasks ==========
  createSiteVisitTask: async (siteVisitData) => {
    return await apiPost('/api/crm/site-visits', siteVisitData);
  },

  // ========== Builder Projects ==========
  getBuilderProjects: async () => {
    try {
      const response = await apiGet('/api/projects');
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching builder projects:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder projects',
        data: []
      };
    }
  },

  getLeadTasks: async (leadId) => {
    return await apiGet(`/api/tasks/lead/${leadId}`);
  },

  /**
   * Get leads registered by agents for the builder
   * @returns {Promise<Object>} - List of leads registered by agents
   */
  getAgentRegisteredLeads: async () => {
    try {
      const response = await apiGet('/api/crm/leads/agent-registered');
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching agent registered leads:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch agent registered leads'
      };
    }
  },

  /**
   * Get leads and stats for a specific agent partnership
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} - Agent leads data with stats and recent leads
   */
  getAgentLeadsForPartnership: async (agentId) => {
    try {
      console.group('🔍 Fetching Agent Leads for Partnership');
      console.log('Agent ID:', agentId);

      const response = await apiGet(`/api/crm/partnerships/${agentId}/leads`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('Raw Response:', response);

      const leadsData = response.data || response;

      console.log('Processed Leads Data:', {
        agentId,
        hasStats: !!leadsData.stats,
        hasLeads: !!leadsData.leads,
        totalLeads: leadsData.stats?.total_leads || 0,
        recentLeadsCount: leadsData.leads?.length || 0
      });

      return {
        success: true,
        data: leadsData,
        message: 'Agent leads fetched successfully'
      };
    } catch (error) {
      console.error('🚨 Agent Leads Error:', {
        agentId,
        errorMessage: error.message,
        fullError: error
      });

      return {
        success: false,
        data: { stats: null, leads: [] },
        message: error.response?.data?.message || error.message || 'Failed to fetch agent leads'
      };
    } finally {
      console.groupEnd();
    }
  },

  /**
   * Get all leads registered by the agent with any builder
   * @returns {Promise<Object>} - List of all leads this agent has registered with builders
   */
  getMyRegisteredLeads: async () => {
    try {
      const response = await apiGet('/api/crm/leads/my-registered-leads');
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching registered leads:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch registered leads'
      };
    }
  },

  /**
   * Toggle agent activation status
   * @param {string} agentId - ID of the agent
   * @param {boolean} activate - Whether to activate or deactivate the agent
   * @returns {Promise<Object>} - Result of the activation/deactivation
   */
  toggleAgentActivation: async (agentId, isActive, notes = null) => {
    try {
      console.group('🔄 Agent Activation Request');
      console.log('Request Parameters:', {
        agentId,
        isActive,
        notes,
        authToken: !!localStorage.getItem('authToken'),
        userRole: localStorage.getItem('userRole'),
        userEmail: localStorage.getItem('userEmail')
      });

      // Validate inputs
      if (!agentId) {
        throw new Error('Agent ID is required');
      }

      // Check authentication token
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Prepare request payload
      const payload = {
        is_active: isActive,
        notes: notes || null
      };

      console.log('Full Request Payload:', payload);

      const response = await apiPut(`/api/crm/partnerships/${agentId}/toggle`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      console.log('Full Response:', response);

      // Additional validation of the response
      if (!response) {
        throw new Error('Received empty response from server');
      }

      // Determine success based on response structure
      const success = response.success !== undefined
        ? response.success
        : (response.status >= 200 && response.status < 300);

      console.log('Response Processing:', {
        success,
        hasData: !!response.data,
        hasMessage: !!response.message
      });

      console.groupEnd();

      return {
        success: success,
        data: response.data || response,
        message: response.message || (success
          ? 'Status updated successfully'
          : 'Failed to update agent activation status')
      };
    } catch (error) {
      console.group('🚨 Agent Activation Error');
      console.error('Detailed Error:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response || 'No response object',
        requestPayload: {
          agentId,
          isActive,
          hasNotes: !!notes
        }
      });
      console.groupEnd();

      return {
        success: false,
        message: error.message || 'Failed to update agent activation status',
        error: {
          message: error.message,
          name: error.name
        }
      };
    }
  },

  /**
   * Get activity history for an agent partnership
   * @param {string} agentId - Agent ID
   * @returns {Promise<Object>} - Activity history array
   */
  getAgentActivityHistory: async (agentId) => {
    console.group('🔍 Fetching Agent Activity History');
    try {
      console.log('🔎 Preparing to fetch activity history for agentId:', agentId);

      // Validate agentId before making the request
      if (!agentId || typeof agentId !== 'string') {
        throw new Error('Valid Agent ID is required for fetching activity history');
      }

      console.log('🔗 Requesting activity history for:', agentId);
      const response = await apiGet(`/api/crm/partnerships/${agentId}/activity`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      console.log('📊 Raw API Response:', response);

      // Validate response structure
      if (!response) {
        throw new Error('No response received from server');
      }

      // Extract activity data with multiple fallback strategies
      const activityData = (
        response.data ||
        (Array.isArray(response) ? response : []) ||
        []
      );

      console.log('📃 Processed Activity Data:', {
        agentId,
        totalActivities: activityData.length,
        firstActivity: activityData[0] || 'No activities'
      });

      return {
        success: true,
        data: activityData,
        message: activityData.length > 0
          ? 'Activity history fetched successfully'
          : 'No activity history found'
      };
    } catch (error) {
      console.error('🚨 Agent Activity History Error:', {
        agentId,
        errorMessage: error.message,
        errorName: error.name,
        fullError: error
      });

      return {
        success: false,
        data: [],
        message: error.response?.data?.message ||
                error.message ||
                'Failed to fetch agent activity history',
        error: {
          message: error.message,
          name: error.name
        }
      };
    } finally {
      console.groupEnd();
    }
  },

  /**
   * Get call history for current user
   * @param {Object} options - Optional filtering and pagination
   * @param {number} [options.limit=50] - Number of records to fetch
   * @param {number} [options.offset=0] - Pagination offset
   * @param {string} [options.type='all'] - Type of calls to fetch
   * @returns {Promise<Object>} - Call history data
   */
  getCallHistory: async (options = {}) => {
    const { limit = 50, offset = 0, type = 'all' } = options;
    try {
      console.group('📞 Call History Request');
      console.log('Call History Options:', { limit, offset, type });

      const response = await apiGet(`/api/calls/history?limit=${limit}&offset=${offset}&type=${type}`);

      console.log('📬 Call History Raw Response:', response);

      return {
        success: true,
        data: response.data || response,
        message: 'Call history fetched successfully'
      };
    } catch (error) {
      console.error('🚨 Call History Error:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch call history'
      };
    } finally {
      console.groupEnd();
    }
  },

  /**
   * Update agent registered lead status (accept/reject)
   * @param {string} leadId - Lead ID
   * @param {Object} data - Status update { status: 'accepted' | 'rejected' }
   * @returns {Promise<Object>} - Update response
   */
  updateAgentLeadStatus: async (leadId, data) => {
    try {
      console.log('🔄 Updating Agent Lead Status:', { leadId, data });

      const response = await apiPut(`/api/crm/leads/agent-registered/${leadId}/status`, data);

      return {
        success: true,
        data: response.data || response,
        message: 'Agent lead status updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating agent lead status:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update agent lead status'
      };
    }
  },

  // ========== Lead Status Configuration ==========
  /**
   * Get all lead statuses for current user
   * @returns {Promise<Object>} - List of lead statuses
   */
  getLeadStatuses: async () => {
    try {
      const response = await apiGet('/api/crm/configurations/lead-statuses');
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch lead statuses'
      };
    }
  },

  /**
   * Create a new lead status
   * @param {Object} statusData - Status data { name, color }
   * @returns {Promise<Object>} - Created lead status
   */
  createLeadStatus: async (statusData) => {
    try {
      const response = await apiPost('/api/crm/configurations/lead-statuses', statusData);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead status created successfully'
      };
    } catch (error) {
      console.error('Error creating lead status:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create lead status'
      };
    }
  },

  /**
   * Update an existing lead status
   * @param {string} statusId - Status ID
   * @param {Object} statusData - Updated status data { name, color }
   * @returns {Promise<Object>} - Updated lead status
   */
  updateLeadStatus: async (statusId, statusData) => {
    try {
      const response = await apiPut(`/api/crm/configurations/lead-statuses/${statusId}`, statusData);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead status updated successfully'
      };
    } catch (error) {
      console.error('Error updating lead status:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update lead status'
      };
    }
  },

  /**
   * Delete a lead status
   * @param {string} statusId - Status ID
   * @returns {Promise<Object>} - Delete confirmation
   */
  deleteLeadStatus: async (statusId) => {
    try {
      const response = await apiDelete(`/api/crm/configurations/lead-statuses/${statusId}`);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead status deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting lead status:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete lead status'
      };
    }
  },

  // ========== Lead Source Configuration ==========
  /**
   * Get all lead sources for current user
   * @returns {Promise<Object>} - List of lead sources
   */
  getLeadSources: async () => {
    try {
      const response = await apiGet('/api/crm/configurations/lead-sources');
      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching lead sources:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || error.message || 'Failed to fetch lead sources'
      };
    }
  },

  /**
   * Create a new lead source
   * @param {Object} sourceData - Source data { name, color }
   * @returns {Promise<Object>} - Created lead source
   */
  createLeadSource: async (sourceData) => {
    try {
      const response = await apiPost('/api/crm/configurations/lead-sources', sourceData);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead source created successfully'
      };
    } catch (error) {
      console.error('Error creating lead source:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create lead source'
      };
    }
  },

  /**
   * Update an existing lead source
   * @param {string} sourceId - Source ID
   * @param {Object} sourceData - Updated source data { name, color }
   * @returns {Promise<Object>} - Updated lead source
   */
  updateLeadSource: async (sourceId, sourceData) => {
    try {
      const response = await apiPut(`/api/crm/configurations/lead-sources/${sourceId}`, sourceData);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead source updated successfully'
      };
    } catch (error) {
      console.error('Error updating lead source:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update lead source'
      };
    }
  },

  /**
   * Delete a lead source
   * @param {string} sourceId - Source ID
   * @returns {Promise<Object>} - Delete confirmation
   */
  deleteLeadSource: async (sourceId) => {
    try {
      const response = await apiDelete(`/api/crm/configurations/lead-sources/${sourceId}`);
      return {
        success: true,
        data: response.data || response,
        message: 'Lead source deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting lead source:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete lead source'
      };
    }
  },

  // ========== Invoice Management (Mobile app exact implementation) ==========

  /**
   * Get lead invoice by lead ID
   */
  getLeadInvoice: async (leadId) => {
    try {
      console.log('🔄 Fetching lead invoice for lead ID:', leadId);
      const response = await apiGet(`/api/crm/invoices/lead/${leadId}`);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching lead invoice:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch lead invoice'
      };
    }
  },

  /**
   * Reject an invoice
   */
  rejectInvoice: async (invoiceId, reason) => {
    try {
      console.log('🔄 Rejecting invoice:', invoiceId, 'with reason:', reason);
      const response = await apiPut(`/api/crm/invoices/${invoiceId}/reject`, {
        rejection_reason: reason
      });

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to reject invoice'
      };
    }
  },

  /**
   * Mark payment as done for an invoice
   */
  markPaymentDone: async (invoiceId, days) => {
    try {
      console.log('🔄 Marking payment as done for invoice:', invoiceId, 'within', days, 'days');
      const response = await apiPut(`/api/crm/invoices/${invoiceId}/payment-done`, {
        payment_days: days
      });

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error marking payment as done:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to mark payment as done'
      };
    }
  },

  // ========== Admin CRM User Management (matching mobile app) ==========

  /**
   * Get full user profile by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Complete user profile data
   */
  getFullProfile: async (userId) => {
    try {
      console.log('🔄 Fetching full profile for user ID:', userId);
      const response = await apiGet(`/api/admin/crm-users/${userId}/full-profile`);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error fetching full profile:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch user profile'
      };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User ID
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} - Updated profile response
   */
  updateProfile: async (userId, profileData) => {
    try {
      console.log('🔄 Updating profile for user ID:', userId, 'with data:', profileData);
      const response = await apiPut(`/api/admin/crm-users/${userId}/profile`, profileData);

      return {
        success: true,
        data: response.data || response
      };
    } catch (error) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to update user profile'
      };
    }
  }
};

export default crmService;